import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Shield, Cpu, Activity } from "lucide-react";

const CrmAi = () => {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConnectors = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_AI_BACKEND_URL}/crm/connectors`);
        const data = await response.json();
        setConnectors(data.connectors || []);
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchConnectors();
  }, []);

  return (
    <div className="min-h-screen bg-[#01040a] text-white font-sans relative overflow-hidden p-6 md:p-12">
      
      {/* --- FLASHY ANIMATION SYSTEM --- */}
      <style>{`
        @keyframes float-particle {
          0% { transform: translateY(0) translateX(0); opacity: 0; }
          50% { opacity: 0.8; }
          100% { transform: translateY(-100vh) translateX(20px); opacity: 0; }
        }
        @keyframes border-draw {
          0%, 100% { clip-path: inset(0 0 95% 0); }
          25% { clip-path: inset(0 95% 0 0); }
          50% { clip-path: inset(95% 0 0 0); }
          75% { clip-path: inset(0 0 0 95%); }
        }
        .cyber-grid {
          background-image: linear-gradient(to right, rgba(34, 211, 238, 0.05) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(34, 211, 238, 0.05) 1px, transparent 1px);
          background-size: 50px 50px;
        }
      `}</style>

      {/* 1. Background Layers */}
      <div className="absolute inset-0 cyber-grid pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#01040a] via-transparent to-[#01040a] pointer-events-none" />
      
      {/* 2. Floating Data Bits */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-cyan-400/40 w-[2px] h-[2px] rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: '100%',
              animation: `float-particle ${Math.random() * 10 + 5}s linear infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      {/* Laser Beam Removed */}

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header Section */}
        <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="animate-in slide-in-from-left duration-700">
            <div className="flex items-center gap-4 mb-4">
               <div className="relative w-14 h-14 bg-[#0a121a] border border-cyan-400/50 rounded-2xl flex items-center justify-center shadow-[0_0_30px_rgba(34,211,238,0.2)]">
                  <Activity className="text-cyan-400 animate-pulse" />
                  <div className="absolute -inset-1 border border-cyan-400/20 rounded-2xl animate-ping" style={{ animationDuration: '3s' }} />
               </div>
               <div>
                 <h1 className="text-4xl font-black tracking-tighter uppercase italic">
                   Neural <span className="text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.5)]">Hub</span>
                 </h1>
                 <p className="text-cyan-400/60 text-xs font-black tracking-[0.4em] uppercase">CRM Connectors</p>
               </div>
            </div>
            <p className="text-slate-400 text-lg max-w-xl font-light italic border-l-2 border-cyan-500/30 pl-6">
              Manage your connections and sync AI capabilities across your platforms.
            </p>
          </div>
          
          <div className="hidden md:block text-right">
             <div className="text-[10px] text-slate-500 font-black tracking-widest uppercase mb-1">System Status</div>
             <div className="flex items-center justify-end gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_#22c55e]" />
                <span className="text-xs font-bold text-green-500 tracking-tighter uppercase">AI Core Online</span>
             </div>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-80 bg-white/5 border border-white/10 rounded-[2.5rem] animate-pulse relative">
                 <div className="absolute inset-0 bg-gradient-to-t from-cyan-500/10 to-transparent" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {connectors.map((name, index) => {
              const isOdoo = name.toLowerCase() === "odoo";
              
              return (
                <div 
                  key={name}
                  style={{ animationDelay: `${index * 150}ms` }}
                  className="animate-in zoom-in-95 fade-in duration-500 group relative bg-[#0a121a]/80 border border-white/10 p-10 rounded-[3rem] transition-all duration-500 hover:border-cyan-400/60 hover:shadow-[0_0_60px_rgba(34,211,238,0.25)] backdrop-blur-xl overflow-hidden"
                >
                  {/* Neon Border Animation (Visible on hover) */}
                  <div className="absolute inset-0 border-2 border-cyan-400 opacity-0 group-hover:opacity-100 group-hover:animate-[border-draw_4s_linear_infinite] pointer-events-none rounded-[3rem]" />
                  
                  {/* Glitch Overlay */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-[0.03] bg-[url('https://www.transparenttextures.com/patterns/asfalt-light.png')] pointer-events-none" />
                  
                  <div className="relative z-10 flex flex-col h-full">
                    <div className="flex justify-between items-start mb-10">
                      {/* Icon Box */}
                      <div className={`w-16 h-16 rounded-[1.5rem] flex items-center justify-center text-3xl font-black shadow-2xl transition-all duration-700 group-hover:rotate-[360deg] group-hover:scale-110
                        ${isOdoo ? "bg-cyan-500 text-[#01040a] shadow-cyan-500/40" : "bg-slate-800 text-slate-500 border border-slate-700"}`}>
                        {isOdoo ? <Cpu size={32} /> : name.charAt(0).toUpperCase()}
                      </div>
                      
                      {/* High-Tech Status */}
                      <div className="flex flex-col items-end">
                        <span className={`text-[10px] font-black uppercase tracking-[0.2em] mb-1
                          ${isOdoo ? "text-cyan-400" : "text-slate-600"}`}>
                          {isOdoo ? "READY" : "LOCKED"}
                        </span>
                        <div className={`h-1 w-12 rounded-full ${isOdoo ? "bg-cyan-400/30" : "bg-slate-800"}`}>
                           <div className={`h-full rounded-full transition-all duration-1000 group-hover:w-full ${isOdoo ? "w-1/2 bg-cyan-400" : "w-0 bg-slate-600"}`} />
                        </div>
                      </div>
                    </div>

                    <h3 className="text-3xl font-black text-white capitalize mb-4 group-hover:tracking-widest transition-all duration-500">
                      {name}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed mb-10 font-light grow group-hover:text-slate-200 transition-colors">
                      {isOdoo 
                        ? "Execute bidirectional data streams between Odoo and your AI Core. Sync leads, contacts, and custom automation nodes."
                        : "Protocol currently under encryption. Specialized AI mapping nodes are being configured for this data source."}
                    </p>

                    {/* Button with Inner Glow */}
                    {isOdoo ? (
                      <Link
                        to={`/dashboard/crm-ai/${name}`}
                        className="relative group/btn overflow-hidden w-full text-center py-5 px-8 rounded-2xl bg-[#01040a] border border-cyan-400 text-cyan-400 font-black text-xs tracking-[0.3em] transition-all duration-300 hover:bg-cyan-400 hover:text-[#01040a] hover:shadow-[0_0_30px_rgba(34,211,238,0.6)]"
                      >
                        <span className="relative z-10 flex items-center justify-center gap-3">
                           <Zap size={14} fill="currentColor" />
                           INITIALIZE CONNECTION
                        </span>
                        <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-all duration-500 group-hover/btn:left-full" />
                      </Link>
                    ) : (
                      <div className="w-full flex items-center justify-center gap-3 py-5 px-8 rounded-2xl bg-white/5 text-slate-600 font-black text-xs tracking-[0.3em] border border-white/5 cursor-not-allowed">
                        <Shield size={14} />
                        ENCRYPTED
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 4. Mouse Glow */}
      <div className="fixed inset-0 pointer-events-none opacity-30 z-0 bg-[radial-gradient(circle_at_var(--x,_50%)_var(--y,_50%),_rgba(34,211,238,0.15)_0%,_transparent_50%)]" 
           onMouseMove={(e) => {
             const x = (e.clientX / window.innerWidth) * 100;
             const y = (e.clientY / window.innerHeight) * 100;
             document.documentElement.style.setProperty('--x', `${x}%`);
             document.documentElement.style.setProperty('--y', `${y}%`);
           }}
      />
    </div>
  );
};

export default CrmAi;